import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
} from "react";
import { auth } from "~src/api";
import { createCheckout } from "~src/api/subscription";
import { setAuthToken, logout as authLogout } from "~src/utils/authService";
import {
  ChromeExtensionEventType,
  sendChromeExtensionMessage,
} from "~src/utils/chromeExtension";

// @types
import { ActionMap, AuthContextType, AuthState, AuthUser } from "~types/auth";

import {logInfo, logError} from "~src/utils/logger"
// ----------------------------------------------------------------------

// CAUTION: User Cognito is slily difference from firebase, so be sure to read the doc carefully.

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

enum Types {
  auth = "AUTHENTICATE",
  register = "REGISTER",
  confirm = "CONFIRM",
  logout = "LOGOUT",
  resetPassword = "RESET_PASSWORD",
}

type AuthPayload = {
  [Types.auth]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.register]: {
    registeringName: string;
    registeringEmail: string;
  };
  [Types.confirm]: {
    confirmedEmail: string;
  };
  [Types.resetPassword]: {
    resettingEmail: string;
  };
  [Types.logout]: undefined;
};

type AuthActions = ActionMap<AuthPayload>[keyof ActionMap<AuthPayload>];

const reducer = (state: AuthState, action: AuthActions) => {
  if (action.type === "AUTHENTICATE") {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      registeredEmail: undefined,
      confirmedEmail: undefined,
      isInitialized: true,
      user,
    };
  }
  if (action.type === "REGISTER") {
    const { registeringEmail, registeringName } = action.payload;
    return {
      ...state,
      registeringName,
      registeringEmail,
    };
  }
  if (action.type === "CONFIRM") {
    const { confirmedEmail } = action.payload;
    return {
      ...state,
      registeringEmail: undefined,
      confirmedEmail,
    };
  }
  if (action.type === "RESET_PASSWORD") {
    const { resettingEmail } = action.payload;
    return {
      ...state,
      resettingEmail,
    };
  }
  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      registeredEmail: undefined,
      confirmedEmail: undefined,
      user: null,
    };
  }
  return state;
};

const AuthContext = createContext<AuthContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getSession = useCallback(async () => {
    let user;
    await auth.me().then((response) => {
      user = response as AuthUser;
      dispatch({
        type: Types.auth,
        payload: { isAuthenticated: true, user: user },
      });
      return {
        user,
      };
    });
  }, []);

  const initial = useCallback(async () => {
    // SaaS shutdown: treat every visitor as unauthenticated so the
    // announcement page (rendered at /login) is always shown, regardless
    // of any stale auth token in localStorage.
    try {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_data");
    } catch {
      // ignore storage errors (e.g. private mode)
    }
    dispatch({
      type: Types.auth,
      payload: {
        isAuthenticated: false,
        user: null,
      },
    });
  }, []);

  useEffect(() => {
    initial();
  }, [initial]);

  // We make sure to handle the user update here, but return the resolve value in order for our components to be
  // able to chain additional `.then()` logic. Additionally, we `.catch` the error and "enhance it" by providing
  // a message that our React components can use.
  const login = useCallback(
    async (email: string, password: string) => {
      const response = await auth.login({
        email,
        password,
      });
      setAuthToken(response.token, {
        id: response.id.toString(),
        username: response.username,
        email: response.email,
        name: response.name,
        role: response.role,
        is_superuser: response.is_superuser,
        is_beta_user: response.is_beta_user
      });
      await getSession();
    },
    [getSession],
  );

  // Update logout to use the new logout() function
  const logout = async () => {
    authLogout(); // This will clear token, user data, and dispatch the auth:logout event
    dispatch({ type: Types.logout });
    await sendChromeExtensionMessage({
      event: ChromeExtensionEventType.LOG_OUT,
      data: {},
    });
  };

  const forgotPassword = async (email: string) => {
    dispatch({ type: Types.resetPassword, payload: { resettingEmail: email } });
  };

  const resetPassword = async () => {
    logInfo("Reset password");
  };

  const changePassword = async (
  ) => {
    logInfo("Change password");
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    recaptchaToken: string,
    priceId: string,
    referralCode?: string
  ) => {
    try {
      // First register the user
      const response = await auth.register(
        name,
        email,
        password,
        recaptchaToken,
      );
      setAuthToken(response.token);

      // Then create checkout session
      if (!priceId) {
        throw new Error("Price ID is required");
      }

      const HOST_API = import.meta.env.VITE_API_HOST || "";
      const isStaging = HOST_API.includes("staging-api.trendrocket.io");

      // Create base payload with only required fields
      const payload = {
        price_id: priceId,
        ...(referralCode && { referral_code: referralCode }),
        ...(!isStaging && { allow_promotion_codes: true }),
      };

      const { payment_url } = await createCheckout(payload);
      window.location.href = payment_url;
    } catch (error) {
      logError(error instanceof Error ? error.message : "Registration failed");
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        user: state.user,
        login,
        register,
        logout,
        forgotPassword,
        resetPassword,
        changePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
